import { Fragment, useCallback, useEffect, useState } from 'react';
import { Badge, Empty, Field, Note, Panel, Spinner, TableWrap, shortId } from '@wyocspay/ui';
import { dateTime, relative } from './format.js';
import { ApiError, api, type MerchantApplication } from './api.js';

/**
 * Заявки за регистрация - входната врата на платформата.
 *
 * Формата на wyocspay.com не създава профил. Тя създава заявка, която човек
 * преглежда тук. Паролата, която кандидатът е избрал, е хеширана още при
 * подаването и не се вижда никъде; чак при „Одобри“ тя ражда истински профил и
 * вход за собственика - затова одобрението изисква правото applications.review.
 *
 * Одобрението дава ВХОД, не право да приема плащания. Дали търговецът ще търгува
 * зависи от проверката на платежния процесор, която е отделна и идва след това.
 */

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'ЧАКА ПРЕГЛЕД',
  INFO_REQUESTED: 'ПОИСКАНА ИНФО',
  APPROVED: 'ОДОБРЕНА',
  REJECTED: 'ОТХВЪРЛЕНА',
};

function statusTone(status: string): 'ok' | 'warn' | 'fail' | 'neutral' {
  if (status === 'APPROVED') return 'ok';
  if (status === 'PENDING' || status === 'INFO_REQUESTED') return 'warn';
  if (status === 'REJECTED') return 'fail';
  return 'neutral';
}

type Tab = 'waiting' | 'all' | 'APPROVED' | 'REJECTED';

export function ApplicationsView({ onOpenMerchant }: { onOpenMerchant?: (id: string) => void }) {
  const [tab, setTab] = useState<Tab>('waiting');
  const [items, setItems] = useState<MerchantApplication[]>([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [openId, setOpenId] = useState<string | null>(null);
  const [mode, setMode] = useState<'info' | 'reject' | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);

  const load = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      const res = await api.listApplications();
      setItems(res.applications);
      setPending(res.pending);
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Live: new applications appear without a reload.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void load(true);
    }, 5000);
    return () => clearInterval(id);
  }, [load]);

  function toggle(id: string): void {
    setMode(null);
    setText('');
    setOpenId((cur) => (cur === id ? null : id));
  }

  async function approve(a: MerchantApplication): Promise<void> {
    setBusy(a.id);
    setMsg(null);
    try {
      const res = await api.approveApplication(a.id);
      setMsg({
        tone: 'ok',
        text: `Профилът на „${a.business_name}“ е създаден и собственикът вече може да влезе с имейла и паролата, които е въвел. Следва проверката от платежния процесор.`,
      });
      setOpenId(null);
      await load();
      if (onOpenMerchant && res.merchant_id) onOpenMerchant(res.merchant_id);
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  async function submitInfo(a: MerchantApplication): Promise<void> {
    if (text.trim().length < 4) return;
    setBusy(a.id);
    setMsg(null);
    try {
      await api.requestInfoApplication(a.id, text.trim());
      setMsg({ tone: 'warn', text: `Изпратено е запитване до ${a.email}. Заявката чака отговора им.` });
      setOpenId(null);
      setMode(null);
      setText('');
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  async function reject(a: MerchantApplication): Promise<void> {
    setBusy(a.id);
    setMsg(null);
    try {
      await api.rejectApplication(a.id, text.trim() || undefined);
      setMsg({ tone: 'warn', text: `Заявката на „${a.business_name}“ е отхвърлена. Кандидатът е уведомен по имейл.` });
      setOpenId(null);
      setMode(null);
      setText('');
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  const rows = items.filter((a) => {
    if (tab === 'all') return true;
    if (tab === 'waiting') return a.status === 'PENDING' || a.status === 'INFO_REQUESTED';
    return a.status === tab;
  });

  const TABS: Array<[Tab, string]> = [
    ['waiting', `Чакат (${pending})`],
    ['all', 'Всички'],
    ['APPROVED', 'Одобрени'],
    ['REJECTED', 'Отхвърлени'],
  ];

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Одобрението създава профила и входа">
        Формата на wyocspay.com подава заявка, не създава профил. При „Одобри“ се създават търговец
        и вход за собственика с имейла и паролата, които кандидатът е въвел - паролата е хеширана и
        никой тук не я вижда. Това дава само достъп до платформата; дали ще приема плащания зависи от
        проверката на платежния процесор, която идва след това. „Поискай информация“ и „Отхвърли“
        изпращат имейл до кандидата.
      </Note>

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`wc-btn wc-btn-sm ${tab === key ? '' : 'wc-btn-secondary'}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => void load()} disabled={loading}>
            Опресни
          </button>
        </div>
      </div>

      {loading ? <Spinner label="Зареждане…" /> : error ? <Note tone="danger">{error}</Note> : null}

      <Panel title={`Заявки · ${rows.length}`} padded={false}>
        {rows.length === 0 && !loading ? (
          <Empty>Няма заявки в тази категория.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Бизнес</th>
                  <th>За контакт</th>
                  <th>Държава</th>
                  <th>Подадена</th>
                  <th>Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const open = openId === a.id;
                  const decided = a.status === 'APPROVED' || a.status === 'REJECTED';
                  return (
                    <Fragment key={a.id}>
                      <tr style={{ opacity: decided ? 0.72 : 1 }}>
                        <td>
                          <strong style={{ fontSize: 13.5 }}>{a.business_name}</strong>
                          <div className="wc-num" style={{ fontSize: 11, color: 'var(--wc-faint)' }}>{shortId(a.id, 5)}</div>
                        </td>
                        <td style={{ fontSize: 13 }}>
                          {a.contact_name}
                          <div style={{ fontSize: 12, color: 'var(--wc-muted)' }}>{a.email}</div>
                        </td>
                        <td className="wc-num" style={{ fontSize: 13 }}>{a.country}</td>
                        <td style={{ fontSize: 13, color: 'var(--wc-muted)' }} title={dateTime(a.created_at)}>
                          {relative(a.created_at)}
                        </td>
                        <td>
                          <Badge tone={statusTone(a.status)}>{STATUS_LABEL[a.status] ?? a.status}</Badge>
                          {a.status === 'APPROVED' && a.merchant_id ? (
                            <div style={{ fontSize: 11.5, marginTop: 3 }}>
                              {onOpenMerchant ? (
                                <button
                                  type="button"
                                  onClick={() => onOpenMerchant(a.merchant_id as string)}
                                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--wc-blue-ink)' }}
                                >
                                  към търговеца →
                                </button>
                              ) : (
                                <span className="wc-num" style={{ color: 'var(--wc-faint)' }}>{shortId(a.merchant_id, 4)}</span>
                              )}
                            </div>
                          ) : null}
                        </td>
                        <td className="wc-right">
                          <button
                            type="button"
                            className="wc-btn wc-btn-sm wc-btn-secondary"
                            onClick={() => toggle(a.id)}
                          >
                            {open ? 'Скрий' : 'Прегледай'}
                          </button>
                        </td>
                      </tr>

                      {open ? (
                        <tr>
                          <td colSpan={6} style={{ background: 'var(--wc-blue-tint, var(--wc-grey-tint))' }}>
                            <div style={{ padding: '8px 0 12px', display: 'grid', gap: 12, maxWidth: 760 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <Detail label="Телефон">{a.phone || '-'}</Detail>
                                <Detail label="Уебсайт">
                                  {a.website ? <span className="wc-num" style={{ wordBreak: 'break-all' }}>{a.website}</span> : '-'}
                                </Detail>
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <Detail label="Адрес">
                                    {[a.address_line1, a.address_line2, [a.postal_code, a.city].filter(Boolean).join(' '), a.region, a.country]
                                      .map((p) => (p ? p.trim() : ''))
                                      .filter(Boolean)
                                      .join(', ') || '-'}
                                  </Detail>
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <Detail label="Съобщение от кандидата">
                                    {a.message ? <span style={{ whiteSpace: 'pre-wrap' }}>{a.message}</span> : '-'}
                                  </Detail>
                                </div>
                                {a.info_request ? (
                                  <div style={{ gridColumn: '1 / -1' }}>
                                    <Detail label="Последно поискана информация">{a.info_request}</Detail>
                                  </div>
                                ) : null}
                                {a.review_note ? (
                                  <div style={{ gridColumn: '1 / -1' }}>
                                    <Detail label="Бележка при отхвърляне">{a.review_note}</Detail>
                                  </div>
                                ) : null}
                                {a.reviewed_at ? (
                                  <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: 'var(--wc-faint)' }}>
                                    Последно решение: {dateTime(a.reviewed_at)}
                                    {a.reviewed_by ? ` · ${shortId(a.reviewed_by, 4)}` : ''}
                                  </div>
                                ) : null}
                              </div>

                              {a.status === 'APPROVED' ? (
                                <Note tone="ok">Одобрена. Профилът и входът вече съществуват.</Note>
                              ) : (
                                <>
                                  {mode === 'info' ? (
                                    <Field label="Каква информация ви трябва?" hint="Изпраща се дословно по имейл до кандидата.">
                                      <textarea
                                        className="wc-input"
                                        rows={3}
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder="напр. линк към сайта или снимка на регистрацията на фирмата"
                                        maxLength={4000}
                                      />
                                    </Field>
                                  ) : null}
                                  {mode === 'reject' ? (
                                    <Field label="Причина за отказ (по желание)" hint="Ако я попълните, се показва в имейла до кандидата.">
                                      <input
                                        className="wc-input"
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder="напр. дейността не се поддържа от процесора"
                                        maxLength={4000}
                                      />
                                    </Field>
                                  ) : null}

                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {mode === null ? (
                                      <>
                                        <button
                                          type="button"
                                          className="wc-btn wc-btn-sm wc-btn-primary"
                                          disabled={busy === a.id}
                                          onClick={() => void approve(a)}
                                        >
                                          {busy === a.id ? 'Одобряване…' : 'Одобри и създай профил'}
                                        </button>
                                        <button
                                          type="button"
                                          className="wc-btn wc-btn-sm wc-btn-secondary"
                                          disabled={busy === a.id}
                                          onClick={() => { setMode('info'); setText(''); }}
                                        >
                                          Поискай информация
                                        </button>
                                        <button
                                          type="button"
                                          className="wc-btn wc-btn-sm wc-btn-danger"
                                          disabled={busy === a.id}
                                          onClick={() => { setMode('reject'); setText(''); }}
                                        >
                                          Отхвърли
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          className={`wc-btn wc-btn-sm ${mode === 'reject' ? 'wc-btn-danger' : 'wc-btn-primary'}`}
                                          disabled={busy === a.id || (mode === 'info' && text.trim().length < 4)}
                                          onClick={() => (mode === 'info' ? void submitInfo(a) : void reject(a))}
                                        >
                                          {busy === a.id
                                            ? 'Изпращане…'
                                            : mode === 'info'
                                              ? 'Изпрати запитването'
                                              : 'Потвърди отхвърлянето'}
                                        </button>
                                        <button
                                          type="button"
                                          className="wc-btn wc-btn-sm wc-btn-secondary"
                                          disabled={busy === a.id}
                                          onClick={() => { setMode(null); setText(''); }}
                                        >
                                          Назад
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Panel>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: 'var(--wc-faint)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 13.5, marginTop: 3 }}>{children}</div>
    </div>
  );
}
